import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { 
  Stethoscope, 
  Search, 
  AlertTriangle, 
  CheckCircle, 
  Info, 
  MessageSquare,
  Send,
  Loader2,
  ChevronRight,
  AlertCircle,
  Shield
} from 'lucide-react';

const HealthAssistantUI = () => {
  const [activeTab, setActiveTab] = useState('chat');
  const [symptoms, setSymptoms] = useState('');
  const [symptomResults, setSymptomResults] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const chatEndRef = useRef(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Scroll to bottom of chat
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const checkSymptoms = async () => {
    if (!symptoms.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const symptomList = symptoms.split(',').map(s => s.trim()).filter(s => s);
      
      const response = await fetch(`${apiUrl}/api/health-assistant/symptom-checker`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ symptoms: symptomList })
      });
      
      if (response.ok) {
        const data = await response.json();
        setSymptomResults(data);
      } else {
        setError('Failed to analyze symptoms. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setLoading(true);
    
    try {
      const response = await fetch(`${apiUrl}/api/health-assistant/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question: userMessage })
      });
      
      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: data.answer,
          disclaimer: data.disclaimer,
          relatedTopics: data.related_topics
        }]);
      } else {
        setChatMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Sorry, I couldn\'t process your question. Please try again.',
          isError: true
        }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Network error. Please check your connection.',
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (riskLevel) => {
    const config = {
      emergency: { color: 'bg-red-600', text: '🚨 Emergency', icon: AlertTriangle },
      urgent: { color: 'bg-orange-500', text: '⚠️ Urgent', icon: AlertCircle },
      moderate: { color: 'bg-yellow-500', text: '📋 Moderate', icon: Info },
      routine: { color: 'bg-green-500', text: '✓ Routine', icon: CheckCircle },
      informational: { color: 'bg-blue-500', text: 'ℹ️ Info', icon: Info }
    };
    
    const { color, text } = config[riskLevel] || config.informational;
    return <Badge className={`${color} text-white`}>{text}</Badge>;
  };

  return (
    <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-blue-50">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-teal-900">
          <Stethoscope className="h-5 w-5 mr-2" />
          Health Assistant
        </CardTitle>
        <p className="text-xs text-gray-500">Science-based health guidance</p>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="chat" className="text-xs">
              <MessageSquare className="h-3 w-3 mr-1" />
              Ask
            </TabsTrigger>
            <TabsTrigger value="symptoms" className="text-xs">
              <Search className="h-3 w-3 mr-1" />
              Symptoms
            </TabsTrigger>
          </TabsList>

          {/* Chat Tab */}
          <TabsContent value="chat" className="space-y-3">
            <ScrollArea className="h-48 border rounded-lg p-2 bg-white">
              {chatMessages.length === 0 ? (
                <div className="text-center py-6 text-gray-400">
                  <Stethoscope className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Ask me about health topics</p>
                  <div className="flex flex-wrap gap-1 justify-center mt-2">
                    {['diabetes', 'headache remedies', 'hydration tips'].map(topic => (
                      <Button
                        key={topic}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => setChatInput(`Tell me about ${topic}`)}
                      >
                        {topic}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-lg p-2 text-sm ${
                        msg.role === 'user' 
                          ? 'bg-teal-600 text-white' 
                          : msg.isError 
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        <div className="whitespace-pre-wrap">{msg.content}</div>
                        {msg.disclaimer && (
                          <p className="text-xs mt-2 opacity-70 border-t pt-1">
                            ⚕️ {msg.disclaimer}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
              )}
            </ScrollArea>
            
            <div className="flex gap-2">
              <Input
                placeholder="Ask a health question..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                className="flex-1"
              />
              <Button 
                onClick={sendChatMessage} 
                disabled={loading || !chatInput.trim()}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </TabsContent>

          {/* Symptom Checker Tab */}
          <TabsContent value="symptoms" className="space-y-3">
            <div className="space-y-2">
              <Input
                placeholder="Enter symptoms (comma separated)"
                value={symptoms}
                onChange={(e) => setSymptoms(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && checkSymptoms()}
              />
              <Button 
                onClick={checkSymptoms}
                disabled={loading || !symptoms.trim()}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing...</>
                ) : (
                  <><Search className="h-4 w-4 mr-2" /> Check Symptoms</>
                )}
              </Button>
            </div>

            {error && (
              <div className="p-2 bg-red-100 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            {symptomResults && (
              <div className="space-y-3 mt-4">
                {/* Risk Level Banner */}
                <div className={`p-3 rounded-lg ${
                  symptomResults.risk_level === 'emergency' ? 'bg-red-100 border-2 border-red-500' :
                  symptomResults.risk_level === 'urgent' ? 'bg-orange-100 border-2 border-orange-500' :
                  symptomResults.risk_level === 'moderate' ? 'bg-yellow-100 border-2 border-yellow-500' :
                  'bg-green-100 border-2 border-green-500'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Risk Assessment</span>
                    {getRiskBadge(symptomResults.risk_level)}
                  </div>
                  
                  {symptomResults.emergency_warning && (
                    <p className="text-red-700 font-bold mt-2 text-sm">
                      ⚠️ Seek immediate medical attention!
                    </p>
                  )}
                </div>

                {/* Possible Conditions */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Possible Conditions:</p>
                  {symptomResults.possible_conditions?.slice(0, 4).map((condition, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                      <span>{condition.name}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {Math.round(condition.confidence * 100)}%
                        </span>
                        {getRiskBadge(condition.risk)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recommendations */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Recommendations:</p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    {symptomResults.recommendations?.map((rec, idx) => (
                      <li key={idx} className="flex items-start">
                        <ChevronRight className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Disclaimer */}
                <div className="flex items-start gap-2 p-2 bg-gray-100 rounded text-xs text-gray-600">
                  <Shield className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <p>{symptomResults.disclaimer}</p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default HealthAssistantUI;
